import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Checkbox, Form, Skeleton } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useQuery } from "@apollo/client";
import { GET_STAFF_MANAGEMENT_ROLES } from "graphql/query/staff-management-gql";
import EditableTagGroup from "components/layout/form-elements/editable-tag-group";
import MultiSelect from "components/layout/form-elements/multi-select";
import { Localize, ModalStandard } from "components/service";
import { useTranslation } from "components/use-hooks";
import {
    getExhibitionWorkerCount,
    getRoleIdsSnapshot,
    normalizeRoleIds,
} from "../../helpers/staff-check-in-setup-helper";

const RoleIdsValue = () => null;

const StaffWorkerRolesField = ({ event, form }) => {
    const modalTitleID = `staff-worker-role-modal-title-${ event?.id }`;
    const translations = useTranslation();
    const removeLabel = translations["GLOBAL.Button_Text_Remove"] || "Remove";
    const initialRoles = useMemo(() => event?.relWorkerRoles || [], [ event?.relWorkerRoles ]);
    const initialRoleIds = normalizeRoleIds(initialRoles);
    const initialRoleSnapshot = getRoleIdsSnapshot(initialRoleIds);
    const dirtyRef = useRef(false);
    const modalOpenRef = useRef(false);
    const draftTouchedRef = useRef(false);
    const [ selectedRoleIds, setSelectedRoleIds ] = useState(initialRoleIds);
    const [ draftRoleIds, setDraftRoleIds ] = useState(initialRoleIds);
    const selectedRoleSnapshot = getRoleIdsSnapshot(selectedRoleIds);
    const { data, loading } = useQuery(GET_STAFF_MANAGEMENT_ROLES, {
        skip: !event?.id,
        variables: {
            eeID: event?.id === null || event?.id === undefined ? undefined : String(event.id),
            first: 999,
        },
        fetchPolicy: "cache-and-network",
        nextFetchPolicy: "cache-first",
    });
    const roles = useMemo(() => {
        const rolesByID = new Map();

        (data?.roles?.data || []).forEach((role) => {
            if (role?.id !== null && role?.id !== undefined)
            {
                rolesByID.set(String(role.id), role);
            }
        });
        initialRoles.forEach((role) => {
            if (
                role?.id !== null &&
                role?.id !== undefined &&
                !rolesByID.has(String(role.id))
            )
            {
                rolesByID.set(String(role.id), role);
            }
        });

        return [ ...rolesByID.values() ];
    }, [ data, initialRoles ]);
    const rolesByID = useMemo(() => new Map(
        roles.map((role) => [ String(role.id), role ])
    ), [ roles ]);
    const selectedTags = selectedRoleIds.map((roleID) => {
        const role = rolesByID.get(roleID) || { id: roleID, label: roleID };

        return {
            value: roleID,
            label: role.label || roleID,
            workerCount: getExhibitionWorkerCount(role),
        };
    });

    const updateSelectedRoles = (roleIds) => {
        const nextRoleIds = normalizeRoleIds(roleIds);

        dirtyRef.current = getRoleIdsSnapshot(nextRoleIds) !== initialRoleSnapshot;
        setSelectedRoleIds(nextRoleIds);
        form.setFieldsValue({ role_id: nextRoleIds });
    };
    const openModalDraft = () => {
        modalOpenRef.current = true;
        draftTouchedRef.current = false;
        setDraftRoleIds(selectedRoleIds);
    };
    const discardModalDraft = () => {
        modalOpenRef.current = false;
        draftTouchedRef.current = false;

        if (dirtyRef.current)
        {
            setDraftRoleIds(selectedRoleIds);
        }
        else
        {
            setSelectedRoleIds(initialRoleIds);
            setDraftRoleIds(initialRoleIds);
            form.setFieldsValue({ role_id: initialRoleIds });
        }
    };
    const handleModalResult = ({ role_id: confirmed }) => {
        const draftTouched = draftTouchedRef.current;

        modalOpenRef.current = false;
        draftTouchedRef.current = false;

        if (confirmed && draftTouched)
        {
            updateSelectedRoles(draftRoleIds);
        }
        else
        {
            discardModalDraft();
        }
    };

    useEffect(() => {
        if (selectedRoleSnapshot === initialRoleSnapshot)
        {
            dirtyRef.current = false;
        }

        if (
            !dirtyRef.current &&
            !modalOpenRef.current &&
            !draftTouchedRef.current
        )
        {
            setSelectedRoleIds(initialRoleIds);
            setDraftRoleIds(initialRoleIds);
            form.setFieldsValue({ role_id: initialRoleIds });
        }
    // The stable snapshot avoids resetting the draft for relation object identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ form, initialRoleSnapshot, selectedRoleSnapshot ]);

    return (
        <div className="staff-worker-role-field">
            <Form.Item
                hidden
                name="role_id"
                initialValue={ initialRoleIds }
                rules={ [ { required: true, type: "array", min: 1 } ] }>
                <RoleIdsValue />
            </Form.Item>

            <EditableTagGroup
                tags={ selectedTags }
                setTags={ updateSelectedRoles }
                truncate={ false }
                tagClassName="staff-worker-role-field__tag"
                closeButtonLabel={ ({ label }) => `${ removeLabel } ${ label }` }
                renderLabel={ ({ label, workerCount }) => (
                    <>
                        <span className="staff-worker-role-field__tag-label" title={ label }>{ label }</span>
                        { workerCount !== null &&
                            <span className="staff-worker-role-field__tag-count">
                                ({ workerCount === 1 ?
                                    <Localize>STAFF_MANAGEMENT.Text_OneWorker</Localize> :
                                    <Localize count={ workerCount }>STAFF_MANAGEMENT.Text_WorkersCount</Localize>
                                })
                            </span>
                        }
                    </>
                ) }>
                <ModalStandard
                    width={ 380 }
                    extraClass="modal-form staff-worker-role-modal"
                    dialogTitle={
                        <span id={ modalTitleID }>
                            <Localize>STAFF_MANAGEMENT.Modal_ChooseGroups</Localize>
                        </span>
                    }
                    dialogLabelledBy={ modalTitleID }
                    onOpen={ openModalDraft }
                    onCancel={ discardModalDraft }
                    modalButton={
                        <Button type="text" className="staff-worker-role-field__add">
                            <PlusOutlined />
                            <Localize>STAFF_MANAGEMENT.Action_AddGroup</Localize>
                        </Button>
                    }>
                    <MultiSelect
                        name="role_id"
                        disableSearch={ true }
                        listHeight={ 310 }
                        formReset={ discardModalDraft }
                        setBtnConfirm={ handleModalResult }>
                        { loading ?
                            <Skeleton active={ true } paragraph={ { rows: 6 } } /> :
                            <Checkbox.Group
                                className="staff-worker-role-modal__options"
                                value={ draftRoleIds }
                                onChange={ (roleIds) => {
                                    draftTouchedRef.current = true;
                                    setDraftRoleIds(normalizeRoleIds(roleIds));
                                } }>
                                { roles.map((role) => (
                                    <Checkbox key={ role.id } value={ String(role.id) }>
                                        <span className="staff-worker-role-modal__role-label">
                                            { role.label || role.id }
                                        </span>
                                    </Checkbox>
                                )) }
                            </Checkbox.Group>
                        }
                    </MultiSelect>
                </ModalStandard>
            </EditableTagGroup>
        </div>
    );
};

export default StaffWorkerRolesField;
